import type { ComponentType } from 'react'

/**
 * How a live screen binds to a route, WITHOUT either side editing the other's files.
 *
 * The lane map gives `src/routes/**` and `src/nav/manifest.ts` to L1-console-shell and
 * `src/screens/fleet/**` and `src/screens/deployment/**` to L2-console-fleet. If flipping a
 * route from `dayN` to `live` meant adding an import here, the screens lane could not do its
 * own job without writing into this one's files, which is exactly the collision
 * `tools/check_lanes.py` exists to prevent - and it would not catch this one, because it
 * compares declared `owns` and not what a session actually needs to touch.
 *
 * So binding is by DISCOVERY, not by registration. A screen lane adds:
 *
 *     src/screens/<area>/<screen>/route.tsx
 *       export const routeId = 'fleet.overview'
 *       export default function FleetOverview() { ... }
 *
 * and nothing in `src/routes/` or `src/nav/` changes except the one `status` field the
 * manifest already documents as the thing that changes.
 *
 * The `status: 'live'` flip in `manifest.ts` stays deliberate and stays here in this lane's
 * file: a screen that exists but is not yet declared live renders its EmptyState, and
 * `routes.check.ts` fails a route declared live with no screen behind it. Discovery decides
 * what CAN render; the manifest decides what DOES.
 *
 * ---------------------------------------------------------------------------------------------
 * DISCOVERY IS IN TWO HALVES, AND THE SPLIT IS wf-12 E-06.
 *
 * This file used to do it in one: `import.meta.glob(..., { eager: true })`, which hands back the
 * modules themselves. That works, and it means EVERY screen module is evaluated at start-up
 * whatever route is being visited — along with everything each one statically imports. One of
 * those imports was `screens/fleet/model/useFleet.ts`, which imported the mock fleet API as a
 * VALUE (it no longer does, wf-12 U-21), so the mock was in the entry graph of every page.
 *
 * The consequence was not a slow first paint. `create-fleet-api.ts` reaches the mock by DYNAMIC
 * import precisely so that a console configured with `VITE_API_BASE_URL` never fetches it, and
 * one static import anywhere collapses that: `vite build` said
 *
 *     mock-fleet-api.ts is dynamically imported by create-fleet-api.ts but also statically
 *     imported by screens/fleet/model/useFleet.ts, dynamic import will not move module into
 *     another chunk
 *
 * and carried on. A console pointed at the real API shipped a 23KB test double and its seed
 * fixtures to the browser, and the only thing that ever noticed was that warning.
 *
 * So the two questions are asked separately, because they have different costs:
 *
 *   WHICH ROUTE DOES EACH SCREEN CLAIM?  Eager, from the SOURCE TEXT — `query: '?raw'` — and a
 *   regex, which is the same technique `routes.check.ts` already uses under plain `node` and for
 *   the same stated reason: "reading the id out of the source rather than importing the module"
 *   means nothing is evaluated. It keeps every property the eager version had: duplicate ids are
 *   still caught at start-up, a module with no `routeId` still fails loudly, and `routeTable.ts`
 *   can still throw for a route declared live with no screen behind it BEFORE anything renders.
 *   Losing that last one was the real risk in this change; it is why the ids stay eager rather
 *   than everything going lazy.
 *
 *   `{ eager: true, import: 'routeId' }` LOOKS like the right tool here and is not. Vite rewrites
 *   it into a static `import { routeId } from './route.tsx'`, so every screen becomes both
 *   statically and dynamically imported and Rollup keeps them all in the entry chunk — the exact
 *   bug this change is fixing, one level up. Measured, not reasoned about: the entry stayed at
 *   916 kB with the mock inside it. The cost of `?raw` is that each `route.tsx`'s source is
 *   inlined as a string, about 110 kB raw across the console; a Vite plugin computing the map at
 *   build time would remove that, and it is recorded as a finding rather than built here.
 *
 *   WHAT DOES IT RENDER?  Lazy. A loader per screen, resolved on first render of that route.
 *
 * WHAT THIS FIXES AND WHAT IT DOES NOT. The mock leaves the entry chunk, so a console
 * configured against `console-api` never loads it — which is what E-06 asserts. It is still in
 * the FLEET chunk, because `useFleet.ts` still imports it statically, so an operator who opens
 * Fleet Overview loads it. The root fix is wf-12 U-21 (where a clock and a change notification
 * live) and then migrating `useFleet.ts`, which is L2-console-fleet's to do.
 * ---------------------------------------------------------------------------------------------
 */
export type ScreenModule = {
  routeId: string
  default: ComponentType
}

/** A screen, not yet loaded. `React.lazy` takes exactly this. */
export type ScreenLoader = () => Promise<ScreenModule>

/** The id a screen claims, read out of its SOURCE. Same regex `routes.check.ts` uses. */
function routeIdIn(source: string): string | undefined {
  return /export const routeId\s*=\s*['"]([^'"]+)['"]/.exec(source)?.[1]
}

type LoaderGlob = Record<string, ScreenLoader>
type IdGlob = Record<string, string | undefined>

/**
 * The route id each screen module claims, WITHOUT evaluating the module.
 *
 * `import: 'routeId'` is the whole trick. Vite rewrites this into a static import of one named
 * binding, so Rollup sees that nothing else from the module is used and the component — and
 * every heavy thing it imports — never enters the entry graph.
 */
function discoverIds(): IdGlob {
  if (injected) return Object.fromEntries(Object.entries(injected).map(([file, mod]) => [file, mod.routeId]))
  try {
    /*
     * Called directly, NOT feature-detected first, and the difference is the whole bug this
     * replaced. `import.meta.glob` is compile time: Vite rewrites this call expression into
     * an object literal and there is never a `glob` FUNCTION at run time, in the browser or
     * anywhere else. So the previous `typeof meta.glob !== 'function'` guard was true even
     * under Vite, and the registry returned {} in every environment including the one it was
     * written for. Nothing caught it because no screen module existed yet: the first screen
     * to go live would have rendered its EmptyState forever, and the manifest's `status`
     * field would have been quietly lying, which is the one property the day-one-manifest
     * idea rests on.
     *
     * Outside a bundler the same line throws, and the catch is the fallback rather than a
     * guard, because "did a build step rewrite this" is not a thing a run time can ask.
     */
    const sources = import.meta.glob<string>('../screens/**/route.tsx', {
      eager: true,
      query: '?raw',
      import: 'default',
    })
    return Object.fromEntries(Object.entries(sources).map(([file, src]) => [file, routeIdIn(src)]))
  } catch {
    return {}
  }
}

/** The loader for each screen module. Lazy: nothing here is evaluated until a route renders. */
function discoverLoaders(): LoaderGlob {
  if (injected) {
    const eager = injected
    return Object.fromEntries(
      Object.entries(eager).map(([file, mod]) => [file, () => Promise.resolve(mod)]),
    )
  }
  try {
    return import.meta.glob('../screens/**/route.tsx') as LoaderGlob
  } catch {
    return {}
  }
}

/**
 * The seam for a runtime that has no bundler.
 *
 * `routes.check.ts` runs under plain `node` so it can assert with nothing installed, and
 * there `import.meta.glob` does not exist at all. Without this, every check that builds the
 * route table fails the moment ANY route goes live, with a message blaming the screen author
 * for a file that is sitting right there. The check scans the directory itself and hands the
 * result in, which makes it a stronger assertion than the glob was: it verifies a live route
 * has a real file on disk claiming it, rather than trusting a bundler it is not running under.
 *
 * It still hands in EAGER modules, and it still should: it reads `routeId` out of the source
 * with a regex and never imports anything, so there is nothing to defer. The loaders below are
 * wrapped around what it gives, which keeps that file unchanged by the lazy split.
 */
let injected: Record<string, ScreenModule> | null = null

export function registerScreens(modules: Record<string, ScreenModule>): void {
  injected = modules
  cache = null
}

let cache: Map<string, ScreenLoader> | null = null

function registry(): Map<string, ScreenLoader> {
  if (cache) return cache
  const ids = discoverIds()
  const loaders = discoverLoaders()
  const found = new Map<string, ScreenLoader>()

  for (const [file, routeId] of Object.entries(ids)) {
    if (typeof routeId !== 'string' || routeId === '') {
      // Loud, not quiet. A screen module that forgot its `routeId` would otherwise bind to
      // nothing and render as an EmptyState, which looks exactly like work not yet done.
      throw new Error(`${file}: a screen module must export \`routeId\` and a default component`)
    }
    if (found.has(routeId)) {
      throw new Error(`${file}: two screen modules both claim routeId "${routeId}"`)
    }
    const load = loaders[file]
    if (!load) {
      // The two globs run over the same pattern, so this cannot happen from a screen author's
      // mistake — only from the patterns drifting apart. Said out loud rather than skipped,
      // because the quiet version is a route that renders nothing for no visible reason.
      throw new Error(`${file}: claims routeId "${routeId}" and no loader was discovered for it`)
    }

    /*
     * The `default` export is checked at LOAD time now rather than at start-up, because that is
     * the first moment the module exists. `React.lazy` would throw on its own, and its message
     * names the element type rather than the file — no use to somebody who has just added a
     * screen and forgotten one line.
     */
    found.set(routeId, async () => {
      const mod = await load()
      if (!mod || !mod.default) {
        throw new Error(`${file}: a screen module must export a default component (routeId "${routeId}")`)
      }
      return mod
    })
  }

  cache = found
  return found
}

/** The loader for the screen bound to a route id, or `null` if no screen has been written yet. */
export function loaderFor(routeId: string): ScreenLoader | null {
  return registry().get(routeId) ?? null
}
